import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { scrollTo } from "@/lib/scroll";
import { EASE } from "@/lib/motion";

const LINKS = [
  { label: "Approach", hash: "#approach" },
  { label: "Services", hash: "#services" },
  { label: "Sectors", hash: "#sectors" },
  { label: "Contact", hash: "#contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (hash) => {
    setOpen(false);
    scrollTo(hash);
  };

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -90, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: EASE }}
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
        scrolled
          ? "border-b border-line bg-white/80 backdrop-blur-md"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <button
          data-testid="logo-home-link"
          onClick={() => window.__lenis?.scrollTo(0)}
          className="group flex items-center gap-3"
        >
          <span className="relative flex h-12 w-12 items-center justify-center">
            <span className="absolute left-0 h-7 w-7 rounded-full border-[2.5px] border-forest transition-transform duration-500 group-hover:translate-x-1.5" />
            <span className="absolute right-0 h-7 w-7 rounded-full border-[2.5px] border-gold transition-transform duration-500 group-hover:-translate-x-1.5" />
          </span>
          <span className="leading-none">
            <span className="block font-serif text-2xl font-semibold tracking-[0.14em] text-zinc-900">
              CONNECT
            </span>
            <span className="mt-1 block font-mono text-[10px] uppercase tracking-[0.32em] text-zinc-500">
              Retail · HoReCa Advisory
            </span>
          </span>
        </button>

        <nav className="hidden items-center gap-8 lg:flex" data-testid="desktop-nav">
          {LINKS.map((l) => (
            <button
              key={l.hash}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              onClick={() => go(l.hash)}
              className="group relative font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-600 transition-colors hover:text-forest"
            >
              {l.label}
              <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-gold transition-all duration-400 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-line bg-white/70 text-zinc-700 lg:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-nav"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="overflow-hidden border-b border-line bg-white/95 backdrop-blur-md lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {LINKS.map((l) => (
                <button
                  key={l.hash}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                  onClick={() => go(l.hash)}
                  className="py-3 text-left font-serif text-xl text-zinc-800 transition-colors hover:text-forest"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="mobile-header-cta-button"
                onClick={() => go("#contact")}
                className="mt-2 mb-3 flex items-center justify-center gap-2 rounded-full bg-forest px-5 py-3 text-sm font-semibold text-alabaster"
              >
                Contact Form <ArrowUpRight className="h-4 w-4" />
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
